import React, {useCallback} from 'react';

import CSSModules from 'react-css-modules';

import styles from './styles.module.scss';

type HandleProps = {
  isStartHandle: boolean;
  value: number;
  videoDuration: number;
  onValueChange: (newValue: number) => void;
  minValue: number;
  maxValue: number;
};

// Step value for slider movement (1 second)
const STEP = 1;

const SliderHandleComponent = ({
  isStartHandle,
  value,
  videoDuration,
  onValueChange,
  minValue,
  maxValue,
}: HandleProps) => {
  const label = isStartHandle ? 'start' : 'end';

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft': {
          e.preventDefault();
          const decreasedValue = Math.round(value - STEP);
          onValueChange(Math.max(minValue, decreasedValue));
          break;
        }
        case 'ArrowRight': {
          e.preventDefault();
          const increasedValue = Math.round(value + STEP);
          onValueChange(Math.min(maxValue, increasedValue));
          break;
        }
        default:
          break;
      }
    },
    [value, minValue, maxValue, onValueChange]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const container = e.currentTarget.parentElement;
      if (!container) {
        return;
      }

      const handleDrag = (moveEvent: MouseEvent) => {
        moveEvent.preventDefault();
        const rect = container.getBoundingClientRect();
        const pos = (moveEvent.clientX - rect.left) / rect.width;
        const rawValue = pos * videoDuration;

        // Force to exact 1-second increments
        const seconds = Math.round(rawValue);

        const newValue = Math.max(minValue, Math.min(maxValue, seconds));
        onValueChange(newValue);
      };

      const stopDrag = () => {
        document.removeEventListener('mousemove', handleDrag);
        document.removeEventListener('mouseup', stopDrag);
      };

      document.addEventListener('mousemove', handleDrag);
      document.addEventListener('mouseup', stopDrag);
    },
    [videoDuration, minValue, maxValue, onValueChange]
  );

  return (
    <button
      styleName={`slider-handle ${label}-handle`}
      style={{left: `${(value / videoDuration) * 100}%`}}
      aria-label={`Set ${label} time: ${Math.round(value)} seconds`}
      aria-valuemin={0}
      aria-valuemax={videoDuration}
      aria-valuenow={value}
      aria-valuetext={`${Math.round(value)} seconds`}
      role='slider'
      tabIndex={0}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
    />
  );
};

export const SliderHandle = CSSModules(SliderHandleComponent, styles, {
  allowMultiple: true,
});
